import {ItemQuantity} from "../State/Inventory";

/**
 * Patch unlocking game logic and utilities
 *
 * This module contains game mechanics, difficulty calculations,
 * and state management for the patch unlocking mini-games.
 */

/** Resource types that use the tree chopping game */
export const TREE_RESOURCES = ["coal", "stone"];

/** Icon size for tree graphics */
export const TREE_ICON_SIZE = 80;

/** Icon size for boulder graphics */
export const BOULDER_ICON_SIZE = 72;

/** Props for the main PatchUnlockModal component */
export type PatchUnlockModalProps = {
    resourceType: string;
    clearedSpots: number;
    isOpen: boolean;
    onClose: () => void;
    onComplete: (rewards: ItemQuantity[]) => void;
    treeHitBonus?: number;
    enableTreeHold?: boolean;
    enableBoulderHold?: boolean;
};

/** State for an individual tree in the chopping game */
export type TreeState = {
    id: number;
    hitsRemaining: number;
    flashing: boolean;
    broken: boolean;
};

/** State for an individual crack in the boulder game */
export type CrackState = {
    id: number;
    hitsRemaining: number;
    flashing: boolean;
    complete: boolean;
};

/** Loot result with display label */
export type LootResult = ItemQuantity & { label: string };

/** Props for the TreeChopGame component */
export type TreeChopGameProps = {
    resourceType: string;
    treeCount: number;
    hitsPerTree: number;
    hitBonus: number;
    holdEnabled: boolean;
    onComplete: (rewards: ItemQuantity[]) => void;
};

/** Props for the BoulderCrackGame component */
export type BoulderCrackGameProps = {
    resourceType: string;
    crackCount: number;
    hitsPerCrack: number;
    holdEnabled: boolean;
    onComplete: (rewards: ItemQuantity[]) => void;
};

/**
 * Calculate how many obstacles (trees or cracks) need to be cleared
 * based on how many patches have already been unlocked
 *
 * @param clearedSpots - Number of patches already cleared
 * @returns Number of obstacles to clear for this patch
 */
export const getTargetCount = (clearedSpots: number): number => {
    return Math.max(1, Math.ceil(Math.pow(1.5, clearedSpots)));
};

/**
 * Calculate how many hits each obstacle requires to clear
 * based on difficulty progression
 *
 * @param clearedSpots - Number of patches already cleared
 * @returns Number of hits required per obstacle
 */
export const getHitsPerTarget = (clearedSpots: number): number => {
    return 5 + clearedSpots * 2;
};

/**
 * Generate the localStorage key for saving progress on a specific patch
 *
 * @param resourceType - Type of resource being mined
 * @returns localStorage key string
 */
export const getProgressKey = (resourceType: string): string => {
    return `patch-progress-${resourceType}`;
};
